import { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuth } from '@/contexts/AuthContext';
import * as NotifService from '@/lib/notificationService';
import { toast } from 'sonner';

const PROMPT_SESSION_KEY = 'notif_prompt_done';

export function NotificationPermissionModal() {
  const { user, profile, updateProfile } = useAuth();
  const [open, setOpen] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (!user || hasChecked.current) return;
    hasChecked.current = true;

    const checkNotifications = async () => {
      const permission = NotifService.getPermission();
      
      // Already granted - just ensure subscription is saved
      if (permission === 'granted') {
        if (!profile?.push_notifications_enabled) {
          const success = await NotifService.subscribe(user.id);
          if (success) {
            await updateProfile({ push_notifications_enabled: true });
          }
        }
        return;
      }

      // Denied - can't do anything
      if (permission === 'denied') return;

      // Already prompted this session
      if (sessionStorage.getItem(PROMPT_SESSION_KEY)) return;

      // Check platform requirements
      const ios = NotifService.isIOS();
      const supported = NotifService.isPushSupported();
      
      if (!supported) return;
      
      if (ios) {
        if (!NotifService.isIOSSupportsPush()) return;
        if (!NotifService.isInstalledPWA()) {
          setShowIOSGuide(true);
          setOpen(true);
          return;
        }
      }

      // Show prompt after short delay
      setTimeout(() => setOpen(true), 1000);
    };

    checkNotifications();
  }, [user, profile, updateProfile]);

  const handleEnable = useCallback(async () => {
    if (!user) return;
    setIsRequesting(true);

    try {
      const granted = await NotifService.requestPermission();
      
      if (granted) {
        const success = await NotifService.subscribe(user.id);
        if (success) {
          await updateProfile({ push_notifications_enabled: true });
          toast.success('Notifications activées !');
        }
        setOpen(false);
        sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
      } else {
        // Check if denied or just dismissed
        const newPermission = NotifService.getPermission();
        if (newPermission === 'denied') {
          toast.error('Notifications bloquées. Modifiez dans les paramètres du navigateur.');
          setOpen(false);
          sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
        } else {
          toast.info('Vous pouvez activer les notifications depuis votre profil.');
          setOpen(false);
          sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
        }
      }
    } catch (error) {
      console.error('[NotificationModal] Error:', error);
    } finally {
      setIsRequesting(false);
    }
  }, [user, updateProfile]);

  const handleSkip = useCallback(() => {
    setOpen(false);
    sessionStorage.setItem(PROMPT_SESSION_KEY, '1');
  }, []);

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleSkip()}>
      <DialogContent className="sm:max-w-sm">
        {showIOSGuide ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Smartphone className="h-5 w-5 text-primary" />
                Installer l'app
              </DialogTitle>
              <DialogDescription>
                Pour recevoir des notifications sur iPhone, installez l'app d'abord.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-sm text-muted-foreground space-y-2">
              <p>1. Appuyez sur <strong>Partager</strong> (📤)</p>
              <p>2. Choisissez <strong>"Sur l'écran d'accueil"</strong></p>
              <p>3. Ouvrez l'app depuis l'écran d'accueil</p>
            </div>
            <Button onClick={handleSkip} className="w-full">Compris</Button>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5 text-primary" />
                Rester informé
              </DialogTitle>
              <DialogDescription>
                Recevez une alerte quand un concierge vous répond.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4 text-sm text-muted-foreground space-y-1">
              <p>✓ Réponses instantanées</p>
              <p>✓ Mises à jour sur vos demandes</p>
            </div>
            <div className="flex flex-col gap-2">
              <Button onClick={handleEnable} disabled={isRequesting} className="w-full">
                {isRequesting ? 'Activation...' : 'Activer les notifications'}
              </Button>
              <Button variant="ghost" onClick={handleSkip} className="w-full text-muted-foreground text-sm">
                Plus tard
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
